import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { isAfter, startOfYesterday } from 'date-fns';
import { styled, keyframes } from '@mui/material/styles';
import { Paper } from '@mui/material';
import { OTP_DELAY_MINUTES, OTP_EARLY_MINUTES } from 'state/constants';
import styles from './WarningBoxes.module.css';
import GanttBar from './GanttBar';


const OBJECT_TOP = 4;
const MAX_WARNING_WIDTH = 720;

const blinkBorder = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
    50% { box-shadow: 0 0 0 2px rgba(158, 208, 230, 0.6), 0 0 4px rgba(97, 172, 134, 0.4); }
    100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
`;

const BarContainer = styled(Paper, {
    shouldForwardProp: (prop) =>
        !['colorType', 'isDragging', 'selected', 'blink', 'scale', 'isChanged'].includes(prop),
})(({ theme, scale, colorType, isDragging, selected, blink, isChanged }) => ({
    zIndex: isDragging ? 99 : 2,
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: `${2 * scale}px ${2 * scale}px`,
    gap: `${0.5 * scale}px`,
    touchAction: 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    backgroundColor:
        colorType === 'International'
        ? theme.palette.blue?.light || '#409adaff'
        : theme.palette.green?.light || '#5bbf63ff',
    borderRadius: 3,
    border: selected
        ? `2px solid ${theme.palette.error.main}`
        : isChanged
        ? `2px dashed ${theme.palette.warning.main}`
        : `1px solid ${theme.palette.divider}`,
    boxShadow: selected
        ? '0 0 4px rgba(190, 87, 185, 0.6)'
        : isDragging
        ? theme.shadows[10]
        : theme.shadows[2],
    animation: blink ? `${blinkBorder} 0.8s ease-in-out 6` : 'none',
    '&:hover': {
        filter: 'brightness(1.05)',
        boxShadow: theme.shadows[4],
        ...(!isDragging && { transform: 'scale(1.02) !important' }),
    },
    '&:active': { cursor: 'grabbing' },
    ...(isDragging && { opacity: 0.8, filter: 'brightness(1.1)', transform: 'none' }),
}));


const WarningLayer = React.memo(({ showStdWarning, showStaWarning, showStaSuccess, warningsWidth, isAnimated }) => {
    const getClassName = (type) => {
        const classes = [styles.baseWarning];
        if (isAnimated) {
            if (type === 'error') classes.push(styles.glow);
            if (type === 'warning') classes.push(styles.breath);
            if (type === 'success') classes.push(styles.ghost);
        }
        return classes.join(' ');
    };

    return (
        <div className={styles.warningLayer}>
            {showStdWarning && (
                <div
                    className={`${getClassName('error')} ${styles.bgError}`}
                    style={{ top: 0, left: `-${warningsWidth.std}px`, width: `${warningsWidth.std}px`, borderRadius: '2px 0 0 2px' }}
                />
            )}
            {showStaWarning && (
                <div
                    className={`${getClassName('warning')} ${styles.bgWarning}`}
                    style={{ bottom: 0, right: 0, width: `${warningsWidth.sta}px`, borderRadius: '0 2px 2px 0' }}
                />
            )}
            {showStaSuccess && (
                <div
                    className={`${getClassName('success')} ${styles.bgInfo}`}
                    style={{ bottom: 0, left: '100%', width: `${warningsWidth.sta}px`, borderRadius: '0 2px 2px 0' }}
                />
            )}
        </div>
    );
});


const DraggableObject = ({
    object,
    selected = false,
    isChanged = false,
    cellWidth = 20,
    rowHeight = 60,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: object.id,
        data: { object },
    });

    const [blink, setBlink] = useState(false);
    const prevSelected = useRef(false);

    const scale = useMemo(() => Math.max(0.2, rowHeight / 60), [rowHeight]);
    const fontSize = useMemo(() => `${0.6 * scale}rem`, [scale]);
    const isAnimated = useMemo(() => isAfter(new Date(object.flightDate), startOfYesterday()), [object.flightDate]);

    const showStdWarning = object.stdDiff > OTP_DELAY_MINUTES;
    const showStaWarning = object.stdDiff <= OTP_DELAY_MINUTES && object.staDiff > OTP_EARLY_MINUTES;
    const showStaSuccess = object.staDiff < -OTP_EARLY_MINUTES;

    const warningsWidth = useMemo(() => ({
        std: Math.min(object.stdDiff * cellWidth / 60, MAX_WARNING_WIDTH),
        sta: Math.min(Math.abs(object.staDiff) * cellWidth / 60, MAX_WARNING_WIDTH),
    }), [object.stdDiff, object.staDiff, cellWidth]);

    const style = useMemo(() => ({
        top: OBJECT_TOP * scale,
        left: object.startSlot * cellWidth + 2,
        width: object.duration * cellWidth - 4,
        height: rowHeight - 2 * (OBJECT_TOP * scale),
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 0 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isDragging ? 'none' : 'transform 0.2s, border 0.2s',
    }), [isDragging, transform, object, cellWidth, rowHeight, scale]);

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        if (!isDragging) onClick?.(object, e);
    }, [isDragging, object, onClick]);

    const handleDoubleClick = useCallback((e) => {
        e.stopPropagation();
        if (!isDragging) onDoubleClick?.(object);
    }, [isDragging, object, onDoubleClick]);

    const handleMouseDown = useCallback((e) => {
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
    }, []);

    const handleMouseEnter = useCallback((e) => {
        if (!isDragging) onMouseEnter?.(e, object);
    }, [isDragging, onMouseEnter, object]);

    useEffect(() => {
        if (selected && !prevSelected.current) {
            setBlink(true);
            const timer = setTimeout(() => setBlink(false), 4800);
            return () => clearTimeout(timer);
        }
        if (!selected) setBlink(false);
        prevSelected.current = selected;
    }, [selected]);

    return (
        <BarContainer
            data-object-id={object.id}
            ref={setNodeRef}
            scale={scale}
            style={style}
            colorType={object.type}
            isDragging={isDragging}
            selected={selected}
            isChanged={isChanged}
            blink={blink}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={onMouseLeave}
            {...listeners}
            {...attributes}
        >
            {!isDragging && (
                <WarningLayer
                    showStdWarning={showStdWarning}
                    showStaWarning={showStaWarning}
                    showStaSuccess={showStaSuccess}
                    warningsWidth={warningsWidth}
                    isAnimated={isAnimated}
                />
            )}
            <GanttBar object={object} fontSize={fontSize} />
        </BarContainer>
    );
};

export default React.memo(DraggableObject, (prev, next) =>
    prev.object.id === next.object.id &&
    prev.selected === next.selected &&
    prev.isChanged === next.isChanged &&
    prev.cellWidth === next.cellWidth &&
    prev.rowHeight === next.rowHeight &&
    prev.object.startSlot === next.object.startSlot &&
    prev.object.duration === next.object.duration
);
